import Header from './components/Header';
import Hero from './components/Hero';
import Footer from './components/Footer';
import {
  faqItems,
  carouselItems,
  cardItems,
  topicItems,
  rankingItems,
  appItem,
  indexItems,
  commentItems,
  mediaItems,
  contactItem,
  updateItems,
  aboutItem,
  featureItems,
} from '@/mocks/homeData';

function SectionHeading({ title, subtitle }: { title: string; subtitle?: string }) {
  return (
    <div className="text-center mb-10 md:mb-14">
      <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
        {title}
      </h2>
      {subtitle && (
        <p className="mt-3 text-sm text-foreground-600 max-w-[600px] mx-auto">
          {subtitle}
        </p>
      )}
    </div>
  );
}

export default function Home() {
  return (
    <>
      <Header />
      <main>
        <Hero />

        {/* #faq - 常见问答 4 items */}
        <section id="faq" className="py-14 md:py-20 bg-background-50">
          <div className="w-full max-w-[960px] mx-auto px-4 md:px-6">
            <SectionHeading title="常见问答" subtitle="关于番茄视频，您想了解的一切都在这里" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
              {faqItems.map((item, idx) => (
                <div
                  key={idx}
                  className="bg-background-100 rounded-lg p-5 md:p-6 border border-background-200/60"
                >
                  <h3 className="text-base font-semibold text-foreground-900 mb-2 flex items-start gap-2">
                    <span className="text-primary-500 shrink-0 mt-0.5">
                      <i className="ri-question-line"></i>
                    </span>
                    {item.question}
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed pl-6">
                    {item.answer}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* #carousel - 焦点推荐 5 items 静态展示 */}
        <section id="carousel" className="py-14 md:py-20 bg-background-100">
          <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6">
            <SectionHeading title="焦点推荐" subtitle="精选热门内容，一站式发现你想看的影视佳作" />
            <div className="flex flex-nowrap gap-4 md:gap-5 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-hide"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {carouselItems.map((item, idx) => (
                <div
                  key={idx}
                  className="flex-none w-[300px] md:w-[360px] snap-start bg-background-50 rounded-lg overflow-hidden border border-background-200/60"
                >
                  <div className="relative w-full aspect-[16/10] bg-background-200 overflow-hidden">
                    <img
                      src={item.imageUrl}
                      alt={item.title}
                      className="w-full h-full object-cover object-top"
                      onError={(e) => {
                        const target = e.currentTarget;
                        target.style.display = 'none';
                        if (target.parentElement) {
                          target.parentElement.classList.add('hidden');
                        }
                      }}
                    />
                    <span className="absolute top-3 left-3 px-2.5 py-1 bg-primary-500 text-background-50 text-xs font-semibold rounded-md">
                      {item.tag}
                    </span>
                  </div>
                  <div className="p-4 md:p-5">
                    <h3 className="text-base font-heading font-bold text-foreground-950 mb-1.5">
                      {item.title}
                    </h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* #cards - 图文信息卡片 5 items */}
        <section id="cards" className="py-14 md:py-20 bg-background-50">
          <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6">
            <SectionHeading title="精选内容" subtitle="五大内容板块，满足您全方位的观影需求" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {cardItems.map((item, idx) => (
                <div
                  key={idx}
                  className="bg-background-100 rounded-lg overflow-hidden border border-background-200/60 group cursor-pointer"
                >
                  <div className="relative w-full aspect-[3/2] bg-background-200 overflow-hidden">
                    <img
                      src={item.imageUrl}
                      alt={item.title}
                      className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      onError={(e) => {
                        const target = e.currentTarget;
                        target.style.display = 'none';
                        if (target.parentElement) {
                          target.parentElement.classList.add('hidden');
                        }
                      }}
                    />
                    <span className="absolute top-3 right-3 px-2 py-0.5 bg-accent-500 text-background-50 text-xs font-bold rounded-md">
                      {item.tag}
                    </span>
                  </div>
                  <div className="p-4 md:p-5">
                    <div className="flex items-center gap-2.5 mb-2">
                      <div className="w-9 h-9 rounded-lg bg-primary-100 flex items-center justify-center">
                        <i className={`${item.icon} text-primary-600 text-base`}></i>
                      </div>
                      <h3 className="text-base font-heading font-bold text-foreground-950">
                        {item.title}
                      </h3>
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                </div>
              ))}
              {/* 填充最后两个位置使 grid 完整 */}
              <div className="hidden lg:block" />
              <div className="hidden lg:block" />
            </div>
          </div>
        </section>

        {/* #topics - 话题标签 11 items */}
        <section id="topics" className="py-14 md:py-20 bg-background-100">
          <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6">
            <SectionHeading title="话题热榜" subtitle="全网热议影视话题，看看大家都在讨论什么" />
            <div className="flex flex-wrap justify-center gap-3 md:gap-4">
              {topicItems.map((topic, idx) => (
                <div
                  key={idx}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-full border cursor-pointer transition-all duration-200 hover:scale-105 whitespace-nowrap"
                  style={{
                    backgroundColor: topic.hot
                      ? 'oklch(var(--primary-100) / 0.8)'
                      : 'oklch(var(--background-200) / 0.8)',
                    borderColor: topic.hot
                      ? 'oklch(var(--primary-300) / 0.6)'
                      : 'oklch(var(--secondary-200) / 0.6)',
                  }}
                >
                  {topic.hot && (
                    <i className="ri-fire-fill text-primary-500 text-xs"></i>
                  )}
                  <span className={`text-sm font-medium ${topic.hot ? 'text-primary-700' : 'text-foreground-700'}`}>
                    {topic.name}
                  </span>
                  <span className={`text-xs ${topic.hot ? 'text-primary-500' : 'text-foreground-500'}`}>
                    {topic.heat}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* #ranking - 热门榜单 7 items */}
        <section id="ranking" className="py-14 md:py-20 bg-background-50">
          <div className="w-full max-w-[960px] mx-auto px-4 md:px-6">
            <SectionHeading title="热门榜单" subtitle="数据驱动的热播排行，找到大家都在追的好内容" />
            <div className="flex flex-col gap-3 md:gap-4">
              {rankingItems.map((item) => (
                <div
                  key={item.rank}
                  className="flex items-center gap-4 md:gap-6 bg-background-100 rounded-lg p-4 md:p-5 border border-background-200/60 cursor-pointer group hover:border-primary-300/60 transition-colors duration-200"
                >
                  <div className="flex items-center justify-center w-10 h-10 md:w-12 md:h-12 rounded-lg shrink-0"
                    style={{
                      backgroundColor: item.rank <= 3
                        ? 'oklch(var(--primary-100))'
                        : 'oklch(var(--secondary-100))',
                    }}
                  >
                    <span className={`text-lg md:text-xl font-heading font-bold ${
                      item.rank <= 3 ? 'text-primary-600' : 'text-secondary-600'
                    }`}>
                      {item.rank}
                    </span>
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-base font-heading font-bold text-foreground-950 group-hover:text-primary-600 transition-colors duration-200 truncate">
                        {item.title}
                      </h3>
                      <span className="px-2 py-0.5 rounded-md text-xs font-semibold whitespace-nowrap"
                        style={{
                          backgroundColor: item.tag === '新剧' ? 'oklch(var(--accent-100))' : 'oklch(var(--primary-100))',
                          color: item.tag === '新剧' ? 'oklch(var(--accent-700))' : 'oklch(var(--primary-700))',
                        }}
                      >
                        {item.tag}
                      </span>
                    </div>
                    <p className="text-sm text-foreground-600 truncate">{item.desc}</p>
                  </div>

                  <div className="shrink-0">
                    {item.change === 'up' && (
                      <i className="ri-arrow-up-line text-primary-500 text-lg"></i>
                    )}
                    {item.change === 'down' && (
                      <i className="ri-arrow-down-line text-secondary-500 text-lg"></i>
                    )}
                    {item.change === 'same' && (
                      <span className="text-secondary-400 text-lg">—</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* #app - 移动观影入口 1 item */}
        <section id="app" className="py-14 md:py-20 bg-background-100">
          <div className="w-full max-w-[960px] mx-auto px-4 md:px-6">
            <div className="bg-background-50 rounded-lg border border-background-200/60 overflow-hidden">
              <div className="flex flex-col lg:flex-row items-center gap-8 p-8 md:p-12">
                <div className="flex-1 flex flex-col gap-5">
                  <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                    {appItem.title}
                  </h2>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {appItem.description}
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    {appItem.features.map((feat, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full bg-primary-100 flex items-center justify-center shrink-0">
                          <i className="ri-check-line text-primary-600 text-xs"></i>
                        </div>
                        <span className="text-sm text-foreground-700">{feat}</span>
                      </div>
                    ))}
                  </div>
                  <a
                    href="#hero"
                    className="inline-flex items-center gap-2 self-start px-6 py-3 bg-primary-500 text-background-50 text-sm font-semibold rounded-md hover:bg-primary-600 transition-colors duration-200 cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-smartphone-line"></i>
                    立即体验移动版
                  </a>
                </div>
                <div className="flex-1 flex justify-center">
                  <div className="w-48 h-80 bg-background-200 rounded-2xl border-4 border-background-300 flex items-center justify-center relative overflow-hidden">
                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 p-4">
                      <div className="w-12 h-12 rounded-xl bg-primary-500 flex items-center justify-center">
                        <i className="ri-play-fill text-background-50 text-xl"></i>
                      </div>
                      <span className="text-xs text-foreground-500 font-medium">番茄视频</span>
                      <span className="text-[10px] text-foreground-400">移动端完美适配</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* #index - 专题合集 8 items */}
        <section id="index" className="py-14 md:py-20 bg-background-50">
          <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6">
            <SectionHeading title="专题合集" subtitle="按题材分类，快速找到你心仪的类型佳作" />
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
              {indexItems.map((item, idx) => (
                <a
                  key={idx}
                  href="#cards"
                  className="block bg-background-100 rounded-lg p-4 md:p-5 border border-background-200/60 cursor-pointer hover:border-primary-300/60 transition-all duration-200 group"
                >
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-heading font-bold text-foreground-900 group-hover:text-primary-600 transition-colors duration-200">
                      {item.title}
                    </h3>
                    <span className="text-xs font-semibold text-primary-600 bg-primary-100 px-2 py-0.5 rounded-md">
                      {item.count}
                    </span>
                  </div>
                  <p className="text-xs text-foreground-600 leading-relaxed">
                    {item.desc}
                  </p>
                  <div className="mt-3 flex items-center gap-1 text-xs text-primary-500 font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                    <span>浏览合集</span>
                    <i className="ri-arrow-right-line"></i>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* #comments - 观众评论 5 items */}
        <section id="comments" className="py-14 md:py-20 bg-background-100">
          <div className="w-full max-w-[960px] mx-auto px-4 md:px-6">
            <SectionHeading title="观众热议" subtitle="真实用户评价，听听番茄视频观众怎么说" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
              {commentItems.map((comment, idx) => (
                <div
                  key={idx}
                  className="bg-background-50 rounded-lg p-5 md:p-6 border border-background-200/60"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full bg-background-200 overflow-hidden shrink-0">
                      <img
                        src={comment.avatar}
                        alt={comment.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          const target = e.currentTarget;
                          target.style.display = 'none';
                        }}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-foreground-900">
                          {comment.name}
                        </span>
                        <span className="text-xs text-foreground-500">{comment.time}</span>
                      </div>
                      <div className="flex items-center gap-0.5 mt-0.5">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <i key={star} className="ri-star-fill text-accent-500 text-xs"></i>
                        ))}
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed">
                    {comment.content}
                  </p>
                  <div className="mt-3 flex items-center gap-1 text-xs text-foreground-500">
                    <i className="ri-thumb-up-line"></i>
                    <span>{comment.likes.toLocaleString()}</span>
                  </div>
                </div>
              ))}
              {/* 最后一行填充 */}
              <div className="hidden md:block" />
            </div>
          </div>
        </section>

        {/* #media - 媒体卡片 5 items */}
        <section id="media" className="py-14 md:py-20 bg-background-50">
          <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6">
            <SectionHeading title="精彩推荐" subtitle="精选高分影视内容，品质好片一网打尽" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {mediaItems.map((item, idx) => (
                <div
                  key={idx}
                  className="bg-background-100 rounded-lg overflow-hidden border border-background-200/60 group cursor-pointer"
                >
                  <div className="relative w-full aspect-video bg-background-200 overflow-hidden">
                    <img
                      src={item.imageUrl}
                      alt={item.title}
                      className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      onError={(e) => {
                        const target = e.currentTarget;
                        target.style.display = 'none';
                        if (target.parentElement) {
                          target.parentElement.classList.add('hidden');
                        }
                      }}
                    />
                    <div className="absolute inset-0 bg-black/10 group-hover:bg-black/20 transition-colors duration-200 flex items-center justify-center">
                      <div className="w-12 h-12 rounded-full bg-background-50/90 flex items-center justify-center">
                        <i className="ri-play-fill text-primary-600 text-xl ml-0.5"></i>
                      </div>
                    </div>
                    <span className="absolute bottom-2 right-2 px-1.5 py-0.5 bg-black/60 text-background-50 text-xs rounded">
                      {item.duration}
                    </span>
                    <span className="absolute top-2 left-2 px-2 py-0.5 text-xs font-semibold rounded-md whitespace-nowrap"
                      style={{
                        backgroundColor: 'oklch(var(--primary-500) / 0.9)',
                        color: 'oklch(var(--background-50))',
                      }}
                    >
                      {item.tag}
                    </span>
                  </div>
                  <div className="p-4">
                    <h3 className="text-sm font-heading font-bold text-foreground-950 mb-2 line-clamp-1">
                      {item.title}
                    </h3>
                    <div className="flex items-center gap-1 text-xs text-foreground-500">
                      <i className="ri-eye-line"></i>
                      <span>{item.views} 次播放</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* #contact - 联系咨询 1 item */}
        <section id="contact" className="py-14 md:py-20 bg-background-100">
          <div className="w-full max-w-[800px] mx-auto px-4 md:px-6">
            <div className="bg-background-50 rounded-lg border border-background-200/60 p-8 md:p-12 text-center">
              <div className="w-16 h-16 rounded-full bg-primary-100 flex items-center justify-center mx-auto mb-5">
                <i className="ri-mail-send-line text-primary-600 text-2xl"></i>
              </div>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
                {contactItem.title}
              </h2>
              <p className="text-sm text-foreground-600 leading-relaxed mb-8 max-w-[500px] mx-auto">
                {contactItem.description}
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 md:gap-8">
                <div className="flex items-center gap-2 text-sm text-foreground-700">
                  <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center">
                    <i className="ri-mail-line text-primary-600"></i>
                  </div>
                  <span className="font-medium">{contactItem.email}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-foreground-700">
                  <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center">
                    <i className="ri-wechat-line text-accent-600"></i>
                  </div>
                  <span className="font-medium">{contactItem.wechat}</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* #updates - 动态时间线 7 items */}
        <section id="updates" className="py-14 md:py-20 bg-background-50">
          <div className="w-full max-w-[800px] mx-auto px-4 md:px-6">
            <SectionHeading title="最新动态" subtitle="番茄视频运营动态，了解平台最新变化与升级" />
            <div className="relative">
              <div className="absolute left-[19px] top-2 bottom-2 w-0.5 bg-background-200"
                style={{ backgroundColor: 'oklch(var(--secondary-200))' }}
              />
              <div className="flex flex-col gap-6">
                {updateItems.map((item, idx) => (
                  <div key={idx} className="flex items-start gap-4 md:gap-6 relative">
                    <div className="w-10 h-10 rounded-full shrink-0 flex items-center justify-center z-10"
                      style={{
                        backgroundColor: idx === 0 ? 'oklch(var(--primary-100))' : 'oklch(var(--background-200))',
                      }}
                    >
                      <div className="w-3 h-3 rounded-full"
                        style={{
                          backgroundColor: idx === 0 ? 'oklch(var(--primary-500))' : 'oklch(var(--secondary-400))',
                        }}
                      />
                    </div>
                    <div className="flex-1 pb-2">
                      <span className="text-xs font-medium text-foreground-500 block mb-1">
                        {item.date}
                      </span>
                      <h3 className="text-sm font-heading font-bold text-foreground-900 mb-1">
                        {item.title}
                      </h3>
                      <p className="text-sm text-foreground-600 leading-relaxed">
                        {item.desc}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* #about - 品牌介绍 1 item */}
        <section id="about" className="py-14 md:py-20 bg-background-100">
          <div className="w-full max-w-[960px] mx-auto px-4 md:px-6">
            <SectionHeading title="关于番茄" subtitle="了解番茄视频的故事与使命" />
            <div className="bg-background-50 rounded-lg border border-background-200/60 p-8 md:p-12">
              <div className="max-w-[720px] mx-auto">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-primary-500 flex items-center justify-center">
                    <i className="ri-play-fill text-background-50 text-2xl"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-heading font-bold text-foreground-950">
                      {aboutItem.title}
                    </h3>
                  </div>
                </div>
                <div className="text-sm text-foreground-700 leading-relaxed space-y-4 whitespace-pre-line">
                  {aboutItem.content}
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-8 pt-8 border-t border-background-200/60">
                  {aboutItem.stats.map((stat, idx) => (
                    <div key={idx} className="text-center">
                      <div className="text-xl md:text-2xl font-heading font-bold text-primary-600">
                        {stat.value}
                      </div>
                      <div className="text-xs text-foreground-500 mt-1">{stat.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* #features - 平台特色 3 items */}
        <section id="features" className="py-14 md:py-20 bg-background-50">
          <div className="w-full max-w-[960px] mx-auto px-4 md:px-6">
            <SectionHeading title="平台特色" subtitle="为什么选择番茄视频？三大核心优势让观影更简单" />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
              {featureItems.map((item, idx) => (
                <div
                  key={idx}
                  className="bg-background-100 rounded-lg p-6 md:p-8 text-center border border-background-200/60 group cursor-pointer hover:border-primary-300/60 transition-colors duration-200"
                >
                  <div className="w-16 h-16 rounded-2xl bg-primary-100 flex items-center justify-center mx-auto mb-5 group-hover:bg-primary-200 transition-colors duration-200">
                    <i className={`${item.icon} text-primary-600 text-2xl`}></i>
                  </div>
                  <h3 className="text-lg font-heading font-bold text-foreground-950 mb-3">
                    {item.title}
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {item.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}